import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    ActionItemWrapper,
    ErrorIconWrapper,
    ErrorMessage,
    InnerElementsWrapper,
    InputWrapper,
    Label,
    LoaderWrapper,
    StyledInput,
    SuccessIconWrapper,
    WhiteShadow,
} from './PromoCodeInput.style';
import { Common, useCommon } from 'src/domains/common/Common';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { makeAutoObservable } from 'mobx';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { validateRequire } from 'src/domains/players/webview/components/ValidatorsNew';
import { validatePromoCode } from 'src/domains/players/webview/components/ValidatorsNew/validatePromoCode';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

export type InputStateType = 'success' | 'apply' | 'error' | 'loading';
const PromoCodeButton = ({
    onValidate,
    hasError,
}: {
    onValidate: () => Promise<void>;
    hasError?: boolean;
}): JSX.Element => {
    return (
        <>
            <Label onClick={onValidate}>
                <I18n
                    langKey='promocode.input.apply'
                    defaultText='Apply'
                />
            </Label>
            {hasError === true ? <ErrorIconWrapper /> : null}
        </>
    );
};
const renderAdditionalSymbols = (
    status: 'error' | 'success' | 'apply' | 'loading',
    onValidate: () => Promise<void>
): JSX.Element | null => {
    switch (status) {
        case 'apply': {
            return <PromoCodeButton onValidate={onValidate} />;
        }
        case 'error': {
            return (
                <PromoCodeButton
                    onValidate={onValidate}
                    hasError
                />
            );
        }
        case 'loading': {
            return <LoaderWrapper size={16} />;
        }
        default: {
            return <SuccessIconWrapper />;
        }
    }
};

class State {
    public status: InputStateType = 'apply';
    public errorMsg: string | null = null;
    public readonly promoCode: FormInputState<string, string>;
    private inputRef: HTMLInputElement | null = null;

    public constructor(
        private readonly common: Common,
        private readonly languagesState: LanguagesState
    ) {
        this.promoCode = FormInputState.new('')
            .map(validateRequire)
            .map(validatePromoCode(this.languagesState.getTranslation));
        makeAutoObservable(this);
    }

    public setInputRef = (ref: HTMLInputElement | null): void => {
        this.inputRef = ref;
    };

    private handleFocus = (): void => {
        if (this.inputRef !== null) {
            this.inputRef.focus();
        }
    };

    private setStatus = (status: InputStateType): void => {
        this.status = status;
    };

    private setErrorMsg = (msg: string | null): void => {
        this.errorMsg = msg;
    };

    public handleChangePromoCode = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.currentTarget.value;
        this.setValueOfPromocode(value);
    };

    public setValueOfPromocode = (value: string): void => {
        this.promoCode.setValue(value);
    };

    public get isInputDisabled(): boolean {
        return this.status === 'error';
    }

    private get promoCodeValue(): string {
        return this.promoCode.value;
    }

    private resetInputAfterMs = (milliseconds: number): void => {
        setTimeout(() => {
            this.setStatus('apply');
            this.setValueOfPromocode('');
            this.setErrorMsg(null);
        }, milliseconds);
    };

    private get getValidationError(): string | null {
        return this.promoCode.errorsForView[0] ?? null;
    }

    private handleValidateError(message: string): void {
        this.setStatus('error');
        this.setValueOfPromocode('');
        this.setErrorMsg(message);
        this.handleFocus();
    }

    public onValidatePromoCode = async (): Promise<void> => {
        this.setStatus('loading');
        this.setErrorMsg(null);
        this.promoCode.setAsVisited();

        const customer = BasicDataModel.get(this.common).basicDataReady;
        const usersCurrency = customer?.currency;
        const usersId = customer?.id;

        if (this.getValidationError !== null) {
            this.handleValidateError(this.getValidationError);
            return;
        }

        if (usersId === undefined || usersCurrency === undefined) {
            this.handleValidateError('Something went wrong.');
            return;
        }

        const response = await this.common.trpcClient.client.promoCodesRouter.apply.mutate({
            accountId: usersId,
            currency: usersCurrency,
            promoCodeId: this.promoCodeValue,
        });

        if (response.status === 201) {
            this.setStatus('success');
            this.resetInputAfterMs(3000);
            return;
        }

        this.handleValidateError(response.body.message);
        return;
    };
}

export const PromoCodeInput = observer('PromoCodeInput', () => {
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const languagesState = LanguagesState.get(common);

    const [state] = useState(() => new State(common, languagesState));

    return (
        <>
            <InnerElementsWrapper
                status={state.status}
                variant={configState.componentVariants.accountSectionVariant}
            >
                <InputWrapper>
                    <StyledInput
                        type='text'
                        placeholder={languagesState.getTranslation('promocode.input.placeholder', 'Have a promo code?')}
                        data-test='promo-code-input'
                        value={state.promoCode.value}
                        onChange={state.handleChangePromoCode}
                        status={state.status}
                        ref={state.setInputRef}
                        variant={configState.componentVariants.accountSectionVariant}
                    />
                    {state.status === 'success' ? null : <WhiteShadow />}
                </InputWrapper>
                <ActionItemWrapper data-test='apply-promo-code'>
                    {renderAdditionalSymbols(state.status, state.onValidatePromoCode)}
                </ActionItemWrapper>
            </InnerElementsWrapper>
            {state.errorMsg === null ? null : <ErrorMessage>{state.errorMsg}</ErrorMessage>}
        </>
    );
});
